/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/label-has-for */
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import bindMethods from 'auto-bind';
import { lighten, darken } from 'polished';
import { withTranslation } from 'react-i18next';
import Mousetrap from 'mousetrap';

import { XOS_RED, DARK_GREY } from '../../constants/colors';
import { clearView, toggleVideoSettings, toggleWebsocketConnect } from '../../state/settings';
import { setVideoSettingsInLocalStorage } from '../../utils/localStorage';
import { getQlikAppId } from '../../state/selectors/getQlikAppId';
import WebsocketConnect from './websocket';

const ModalContainer = styled.div`
  display: ${props => (props.active ? 'block' : 'none')};
  position: fixed;
  z-index: 1000;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.4);
`;

const ModalContent = styled.div`
  background-color: #fefefe;
  margin: 15% auto;
  padding: 20px;
  border: 1px solid #888;
  width: 50%;
`;

const ModalFooter = styled.div`
  display: flex;
  padding-top: 20px;
  justify-content: flex-end;
  align-items: center;
`;

const ModalTitle = styled.div`
  display: block;
  position: relative;
  margin: 0 auto;
  text-align: center;
  font-weight: 600;
  font-size: 20px;
  margin-bottom: 20px;
`;

const SettingsContent = styled.div`
  display: flex;
  position: relative;
  flex-direction: row;
  padding: 10px;
`;

const SettingsRow = styled.div`
  display: flex;
  align-self: stretch;
  flex-direction: column;
  justify-content: center;
  width: 50%;
`;

const SettingsItem = styled.div`
  display: flex;
  padding: 10px;
`;

const ModalButton = styled.button`
  display: block;
  width: 110px;
  border: none;
  border-radius: 2px;
  font-size: 16px;
  height: 40px;
  background-color: ${props => (props.type === 'closing' ? XOS_RED : DARK_GREY)};
  color: #fff;
  cursor: pointer;

  :disabled {
    cursor: not-allowed;
    background: ${lighten(0.2, DARK_GREY)};
  }

  :hover {
    background: ${props => props.disabled
    ? null
    : props.type === 'closing'
      ? darken(0.05, XOS_RED)
      : darken(0.05, DARK_GREY)};
  }
`;

const RadioButtonContainer = styled.span`
  display: flex;
  padding-right: 8px;
`;

class FootballSettingsModal extends PureComponent {
  constructor(props) {
    super(props);
    bindMethods(this);
    this.keyboardShortcut = ['esc'];
  }

  componentDidMount() {
    const { clearView } = this.props;
    Mousetrap.bind(this.keyboardShortcut, clearView);
  }

  componentWillUnmount() {
    Mousetrap.unbind(this.keyboardShortcut);
  }

  saveVideoSettings() {
    const { thunderDefault, toggleVideoSettings } = this.props;
    setVideoSettingsInLocalStorage({ thunderDefault: !thunderDefault });
    toggleVideoSettings(!thunderDefault);
  }

  loadApplicationData() {
    const { appId, websocketConnect, toggleWebsocketConnect } = this.props;

    if (!appId) return;

    toggleWebsocketConnect(!websocketConnect);
  }

  render() {
    const {
      active, clearView, thunderDefault, t, appId, websocketConnect,
    } = this.props;

    return (
      <ModalContainer id="GeneralSettings.ApplicationModal" active={active}>
        <ModalContent>
          <ModalTitle>{t('NavigationBar.User.General.Title')}</ModalTitle>
          {websocketConnect ? (
            <WebsocketConnect />
          ) : (
            <React.Fragment>
              <SettingsContent>
                <SettingsRow>
                  <SettingsItem>{t('NavigationBar.User.General.Video')}</SettingsItem>
                </SettingsRow>
                <SettingsRow>
                  <SettingsItem>
                    <RadioButtonContainer>
                      <input
                        type="radio"
                        id="thunder"
                        value="thunder"
                        checked={thunderDefault}
                        onChange={this.saveVideoSettings}
                      />
                      <label htmlFor="thunder">{t('NavigationBar.User.General.Thunder')}</label>
                    </RadioButtonContainer>
                    <RadioButtonContainer>
                      <input
                        type="radio"
                        id="cloud"
                        value="cloud"
                        checked={!thunderDefault}
                        onChange={this.saveVideoSettings}
                      />
                      <label htmlFor="cloud">{t('NavigationBar.User.General.Cloud')}</label>
                    </RadioButtonContainer>
                  </SettingsItem>
                </SettingsRow>
              </SettingsContent>
              <SettingsContent>
                <SettingsRow>
                  <SettingsItem>Application Data</SettingsItem>
                </SettingsRow>
                <SettingsRow>
                  <SettingsItem>
                    <ModalButton
                      disabled={!appId}
                      id="GeneralSettings.ApplicationModal.LoadData"
                      type="loading"
                      onClick={this.loadApplicationData}
                    >
                        Load Data
                    </ModalButton>
                  </SettingsItem>
                </SettingsRow>
              </SettingsContent>
              <ModalFooter>
                <ModalButton
                  id="GeneralSettings.ApplicationModal.Close"
                  type="closing"
                  onClick={() => clearView()}
                >
                        Close
                </ModalButton>
              </ModalFooter>
            </React.Fragment>
          )}
        </ModalContent>
      </ModalContainer>
    );
  }
}

FootballSettingsModal.propTypes = {
  t: PropTypes.func.isRequired,
  thunderDefault: PropTypes.bool.isRequired,
  toggleVideoSettings: PropTypes.func.isRequired,
  active: PropTypes.bool.isRequired,
  clearView: PropTypes.func.isRequired,
  appId: PropTypes.string.isRequired,
  websocketConnect: PropTypes.bool.isRequired,
  toggleWebsocketConnect: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  thunderDefault: state.settings.thunder.default,
  appId: getQlikAppId(state),
  websocketConnect: state.settings.websocket.connect,
});

const mapDispatchToProps = {
  clearView,
  toggleVideoSettings,
  toggleWebsocketConnect,
};

const TranslatedComponent = withTranslation()(FootballSettingsModal);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(TranslatedComponent);
